// ============================================================================
// DECLARATION DATA — Declaração de Hipossuficiência Econômica (Padrão Ouro)
// Sincronizado com o mestre do Firestore
// (declaration_templates/tpl_hipossuficiencia_v2) em 12/08/2026.
// Sessão de auditoria cruzada Procuração/Declaração/Contrato — ver
// CONSTITUICAO_REGRAS.SH Art. 337 (runbook local<->nuvem), Art. 345
// (fix do preâmbulo e do resolveText) e Art. 349-350 (análise cruzada).
// Base legal (texto da declaração): CF Art. 5º, LXXIV + CPC Arts. 98, 99 §§2-3 + LGPD Art. 7º
// Jurisprudência de apoio (STJ Tema 1178, REsp 2.163.244/SP, HC 261.074-MS) NÃO é mais
// citada no corpo da declaração (decisão de 06/08/2026) — pertence à petição inicial,
// não a uma declaração pessoal de fato assinada pelo cliente.
//
// GÊNERO DO TERMO INSTITUCIONAL ("o/a DECLARANTE") — VARIÁVEL, ÚNICA EXCEÇÃO
// DA FAMÍLIA. Ver CONSTITUICAO_REGRAS.SH Art. 381 para a análise completa.
// Resumo: só é seguro ser variável porque `clientData` é objeto ÚNICO
// (useState(null), sem array) — a Declaração é, por natureza, uma declaração
// pessoal e intransferível, nunca tem mais de 1 declarante. Contrato,
// Procuração e os 2 Termos de mandato ficam fixos (singular e feminino,
// invariável) porque Contrato/Procuração já suportam múltiplos signatários
// reais hoje, e os 2 Termos seguem o mesmo padrão por consistência/futuro-
// proofing. Se um dia esta Declaração ganhar suporte a múltiplos
// declarantes, ela deixa de poder ser variável e precisa migrar para o
// padrão fixo — nunca o contrário.
// ============================================================================

import {
  generateQualificationText,
  buildCanonicalClientQualification,
} from "../../config/ClientSchema";
import { escapeHtml } from "../../utils/escapeHtml";

export const HIPOSSUFICIENCIA_REASONS = [
  { code: "A", label: "Desemprego / Sem renda fixa" },
  { code: "B", label: "Superendividamento" },
  { code: "C", label: "Custos extraordinários de saúde" },
  { code: "D", label: "Economia do cuidado / Gênero" },
  { code: "E", label: "Renda incompatível com encargos processuais" },
  { code: "F", label: "PCD / Mantenedor(a) único(a) de dependente PCD" },
];

export const DECLARATION_TYPES = [
  {
    id: "hipossuficiencia",
    label: "Declaração de Hipossuficiência Econômica",
    subtitle: "Gratuidade da Justiça — CPC Art. 98-99",
    icon: "fa-hand-holding-heart",
  },
];

// ── DECLARATION_STRUCTURE — Formato Padrão Ouro (idêntico a ContractData) ────
// Mesmo schema: { id, title, type, tags, condition, icon, content?, children? }
export const DECLARATION_STRUCTURE = [
  {
    id: "decl_preambulo",
    title: "PREÂMBULO E DIRETRIZES DE INTERPRETAÇÃO",
    type: "text",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-gavel",
    content:
      "<p>Considerando que a hipossuficiência econômica é causa legítima para o reconhecimento do direito à gratuidade da justiça, nos termos do art. 5º, inciso LXXIV, da Constituição Federal de 1988, e dos arts. 98 e seguintes da Lei n. 13.105/2015 (Código de Processo Civil);</p><p>Considerando que a declaração de insuficiência de recursos, conforme previsto no art. 99, § 3º, da Lei n. 13.105/2015 (Código de Processo Civil), presume-se verdadeira até prova em contrário, impondo-se {{ao_DECLARANTE}} responsabilidade civil por eventual má-fé, nos termos dos arts. 79 a 81 do mesmo Código de Processo Civil;</p><p>{{ARTIGO_UC}} <strong>DECLARANTE</strong> firma a presente declaração de hipossuficiência econômica com o propósito de fundamentar o pedido de gratuidade da justiça no processo a que se destina, visando garantir o acesso à justiça e a tutela de direitos fundamentais, em conformidade com a legislação vigente.</p><p>Para fins de padronização e fluidez de leitura, a presente declaração utilizará o substantivo <strong>DECLARANTE</strong> com artigo e adjetivos concordantes com o gênero gramatical indicado na Cláusula 1ª — isto é, <strong>o DECLARANTE</strong> ou <strong>a DECLARANTE</strong> conforme o caso — para referir-se à pessoa que subscreve este documento. Toda a concordância verbal e nominal das cláusulas a seguir obedecerá estritamente a esta regra.</p><p>Ressalva-se que a padronização de gênero gramatical aqui adotada tem finalidade exclusivamente técnica de uniformização redacional, não refletindo qualquer declaração sobre o gênero {{do}} <strong>DECLARANTE</strong>, assegurando-se a validade da declaração independentemente de eventuais usos gramaticais divergentes da concordância tradicional.</p>",
  },
  {
    id: "decl_cl01_declarante",
    title: "{{DO_TITLE}} Declarante",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-user",
    children: [
      {
        id: "decl_cl01_01",
        type: "sub_clause",
        content: "<strong>DECLARANTE:</strong> {{declaranteQual}}",
        condition: "ALWAYS",
        level: 0,
      },
    ],
  },
  {
    id: "decl_cl02_insuficiencia",
    title: "Da Insuficiência de Recursos e do Mínimo Existencial",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-hand-holding-usd",
    children: [
      {
        id: "decl_cl02_01",
        type: "sub_clause",
        content:
          "{{ARTIGO_UC}} <strong>DECLARANTE</strong>, devidamente {{qualificado}} na Cláusula 1ª deste instrumento, <strong>AFIRMA</strong>, sob as penas da lei, para os fins e efeitos do art. 5º, inciso LXXIV, da Constituição Federal, c/c o art. 99, § 3º, da Lei n. 13.105/2015 (Código de Processo Civil), que <strong>se encontra em estado de insuficiência de liquidez financeira atual</strong>, consistente na impossibilidade de arcar com as custas, despesas processuais e honorários advocatícios sucumbenciais sem comprometimento do seu sustento próprio e de sua família. Referida insuficiência decorre da incompatibilidade entre a renda líquida disponível {{do}} <strong>DECLARANTE</strong> e os encargos do processo, e não se resume à ausência de patrimônio imobilizado.",
        condition: "ALWAYS",
        level: 0,
      },
      {
        id: "decl_cl02_02",
        type: "sub_clause",
        content: "A hipossuficiência decorre faticamente de:",
        condition: "HAS_ANY_REASON",
        level: 0,
      },
      {
        id: "decl_cl02_reason_A",
        type: "sub_clause",
        content:
          "Desemprego estrutural ou ausência de renda fixa capaz de garantir o mínimo existencial.",
        condition: "HAS_REASON_A",
        level: 1,
      },
      {
        id: "decl_cl02_reason_B",
        type: "sub_clause",
        content:
          "Superendividamento crônico — Lei n. 14.181/2021 (Lei do Superendividamento), em que descontos e repactuações consomem a integralidade da renda líquida mensal disponível.",
        condition: "HAS_REASON_B",
        level: 1,
      },
      {
        id: "decl_cl02_reason_C",
        type: "sub_clause",
        content:
          "Elevados custos extraordinários e contínuos com saúde, medicamentos ou terapias para {{artigo}} <strong>DECLARANTE</strong> ou seus dependentes legais.",
        condition: "HAS_REASON_C",
        level: 1,
      },
      {
        id: "decl_cl02_reason_D",
        type: "sub_clause",
        content:
          "Dificuldade de reintegração no mercado de trabalho remunerado em razão do exercício do trabalho de economia do cuidado não remunerado (Resolução CNJ n. 492/2023 — Protocolo para Julgamento com Perspectiva de Gênero).",
        condition: "HAS_REASON_D",
        level: 1,
      },
      {
        id: "decl_cl02_reason_E",
        type: "sub_clause",
        content:
          "Renda insuficiente para custear a demanda: embora possua vínculo empregatício ativo e renda mensal, a remuneração líquida percebida {{pelo}} <strong>DECLARANTE</strong> é integralmente absorvida pelas despesas ordinárias de subsistência própria e de sua família, tornando impossível arcar com custas, despesas processuais e honorários sucumbenciais sem comprometimento do mínimo existencial.",
        condition: "HAS_REASON_E",
        level: 1,
      },
      {
        id: "decl_cl02_reason_F",
        type: "sub_clause",
        content:
          "Condição de pessoa com deficiência (PCD), própria ou de dependente sob cuidado: {{ARTIGO_UC}} <strong>DECLARANTE</strong> é Pessoa com Deficiência (PCD) e/ou é {{artigo_unico}} mantenedor{{artigo_a}} de dependente(s) com deficiência (incluindo Transtorno do Espectro Autista — TEA), arcando individualmente com a totalidade das despesas de subsistência, saúde, educação especial, terapias e acompanhamentos multidisciplinares do núcleo familiar, o que agrava substancialmente a insuficiência de recursos — nos termos do art. 5º, inciso LXXIV, da Constituição Federal, da Lei n. 13.146/2015 (Estatuto da Pessoa com Deficiência) e da Lei n. 12.764/2012 (Lei Berenice Piana — Proteção dos Direitos da Pessoa com TEA).",
        condition: "HAS_REASON_F",
        level: 1,
      },
    ],
  },
  {
    id: "decl_cl03_consentimento",
    title: "Do Consentimento Prévio e Autorização de Pesquisa Patrimonial",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-search-dollar",
    children: [
      {
        id: "decl_cl03_01",
        type: "sub_clause",
        content:
          "{{ARTIGO_UC}} <strong>DECLARANTE</strong> <strong>AUTORIZA</strong>, de forma expressa e voluntária, para os fins deste processo, que o juízo competente promova pesquisas patrimoniais nos sistemas institucionais do Poder Judiciário, incluindo o Sistema Nacional de Investigação Patrimonial e Recuperação de Ativos — <strong>SNIPER</strong> (CNJ), o <strong>INFOJUD</strong> (Receita Federal), o <strong>SISBAJUD</strong> (Banco Central) e o <strong>RENAJUD</strong> (SENATRAN), independentemente de prévia decretação de quebra de sigilo bancário ou fiscal. Este consentimento é prestado {{pelo}} <strong>DECLARANTE</strong> por ter convicção plena da veracidade das informações, reconhecendo que as consultas realizadas poderão confirmar a lisura do presente pleito.",
        condition: "ALWAYS",
        level: 0,
      },
      {
        id: "decl_cl03_02_openfinance",
        type: "sub_clause",
        content:
          "Sem prejuízo do quanto disposto no item anterior, {{ARTIGO_UC}} <strong>DECLARANTE</strong> <strong>AUTORIZA</strong>, de igual modo, a consulta pelo juízo aos dados cadastrais e transacionais disponibilizados pelo sistema de Open Finance (Resolução Conjunta BCB/CVM n. 1/2020) e a terceiros legalmente habilitados, com a finalidade exclusiva de aferir a coerência entre a renda e o padrão de vida declarados nesta peça, reconhecendo {{ARTIGO_UC}} <strong>DECLARANTE</strong> que eventual incompatibilidade não sanada poderá ensejar o indeferimento do pedido de gratuidade, sem prejuízo das sanções por litigância de má-fé já mencionadas.",
        condition: "ALWAYS",
        level: 0,
      },
    ],
  },
  {
    id: "decl_cl04_lgpd",
    title: "Da Autorização de Juntada de Documentos e Conformidade com a LGPD",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-file-contract",
    children: [
      {
        id: "decl_cl04_01",
        type: "sub_clause",
        content:
          "{{ARTIGO_UC}} <strong>DECLARANTE</strong> <strong>AUTORIZA</strong>, para os fins do art. 99, § 2º, da Lei n. 13.105/2015 (Código de Processo Civil), que o escritório <strong>{{advFirma}}</strong>, OAB n. {{advOab}}, junte aos presentes autos os documentos comprobatórios de sua condição econômica que se mostrarem necessários à instrução deste pedido de gratuidade, incluindo declarações de imposto de renda, extratos bancários, holerites e demais documentos financeiros. Para os fins da Lei n. 13.709/2018 (LGPD), esta autorização constitui, cumulativamente:",
        condition: "ALWAYS",
        level: 0,
      },
      {
        id: "decl_cl04_02",
        type: "sub_clause",
        content:
          "consentimento informado para tratamento de dados pessoais e financeiros (art. 7º, inciso I, da Lei n. 13.709/2018 — LGPD); e",
        condition: "ALWAYS",
        level: 1,
      },
      {
        id: "decl_cl04_03",
        type: "sub_clause",
        content:
          "exercício regular de direitos em processo judicial (art. 7º, inciso VI, da Lei n. 13.709/2018 — LGPD).",
        condition: "ALWAYS",
        level: 1,
      },
      {
        id: "decl_cl04_04",
        type: "sub_clause",
        content:
          "{{ARTIGO_UC}} <strong>DECLARANTE</strong> está ciente de que, após o peticionamento eletrônico nos sistemas do Poder Judiciário (PJe, Projudi, Eproc), a custódia dos documentos juntados aos autos migra para o Estado, cessando a responsabilidade tecnológica do escritório por eventuais incidentes originados nos servidores dos tribunais.",
        condition: "ALWAYS",
        level: 0,
      },
    ],
  },
  {
    id: "decl_cl05_firewall",
    title: "Da Assunção Exclusiva de Responsabilidade e Firewall Ético",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-shield-alt",
    children: [
      {
        id: "decl_cl05_intro",
        type: "sub_clause",
        content:
          "{{ARTIGO_UC}} <strong>DECLARANTE</strong> está ciente de que:",
        condition: "ALWAYS",
        level: 0,
      },
      {
        id: "decl_cl05_01",
        type: "sub_clause",
        content:
          "O juízo <em>poderá</em> exigir a comprovação da insuficiência de recursos, intimando {{artigo}} <strong>DECLARANTE</strong> antes de qualquer indeferimento (art. 99, §§ 2º e 3º, da Lei n. 13.105/2015 — Código de Processo Civil);",
        condition: "ALWAYS",
        level: 1,
      },
      {
        id: "decl_cl05_02",
        type: "sub_clause",
        content:
          "A prestação de informações falsas ou a omissão dolosa de patrimônio configura <strong>litigância de má-fé</strong>, sujeitando {{artigo}} <strong>DECLARANTE</strong> às sanções dos arts. 79 a 81 da Lei n. 13.105/2015 (Código de Processo Civil) — incluindo multa de até 10% do valor da causa e indenização à parte adversa — e à responsabilidade civil por perdas e danos nos termos do art. 186 da Lei n. 10.406/2002 (Código Civil);",
        condition: "ALWAYS",
        level: 1,
      },
      {
        id: "decl_cl05_03",
        type: "sub_clause",
        content:
          "Os advogados do escritório <strong>{{advFirma}}</strong> atuaram com base exclusivamente nas informações fornecidas {{pelo}} <strong>DECLARANTE</strong>, não lhes competindo a auditoria prévia de seu patrimônio; <strong>{{ARTIGO_UC}} DECLARANTE assume integralmente e com exclusividade</strong> qualquer responsabilidade por inveracidades informadas, isentando os patronos de solidariedade em quaisquer sanções processuais decorrentes;",
        condition: "ALWAYS",
        level: 1,
      },
      {
        id: "decl_cl05_04",
        type: "sub_clause",
        content:
          "A presente declaração produz efeitos para o processo a que se destina, podendo ser exigida nova declaração em processos futuros.",
        condition: "ALWAYS",
        level: 1,
      },
    ],
  },
];

// ── Helpers para placeholders ────────────────────────────────────────────────
export function buildPlaceholderVars({ clientData, officeData }) {
  const isPlaceholder = !clientData;
  const advNome = String(officeData?.name || "RODRIGO DEMETERCO").trim();
  const advOab = String(officeData?.oab || "113.938/PR").trim();
  const advFirma = advNome + " Advocacia";

  const clientGender = isPlaceholder
    ? ""
    : String(
        clientData?.grammaticalGender ||
          clientData?.qualification?.physical?.grammaticalGender ||
          "",
      )
        .trim()
        .toUpperCase();

  const qualificadoAdj = clientGender === "F" ? "qualificada" : "qualificado";
  const artigo = clientGender === "F" ? "a" : "o";
  const artigoUC = clientGender === "F" ? "A" : "O";
  const pelo = clientGender === "F" ? "pela" : "pelo";
  const doDecl = clientGender === "F" ? "da" : "do";

  // Qualificação canônica — mesma fonte que Contrato/Procuração usam
  // (buildCanonicalClientQualification, ClientSchema.js), nunca lógica
  // manual local. O texto já vem com ponto final (withPeriod:true dentro de
  // generateQualificationText) — não stripar/reinserir ponto aqui; isso já
  // foi feito antes por engano (Art. 387/gambiarra do ponto final) e foi
  // corrigido em 27/08/2026. "condição de saúde declarada" é removida por
  // ser dado administrativo interno, não pertence à qualificação jurídica
  // formal — a remoção não afeta o ponto final, que vem depois dela.
  let declaranteQual = "...";
  if (!isPlaceholder) {
    const rawQual = String(
      buildCanonicalClientQualification(clientData, {})?.text || "",
    ).trim();
    declaranteQual = rawQual.replace(
      /,?\s*condição de saúde declarada:\s*[^,.]+/gi,
      "",
    );
  }

  return {
    "{{artigo}}": artigo,
    "{{ARTIGO_UC}}": artigoUC,
    "{{pelo}}": pelo,
    "{{do}}": doDecl,
    "{{DO}}": doDecl.toUpperCase(),
    "{{DO_TITLE}}": clientGender === "F" ? "Da" : "Do",
    "{{qualificado}}": qualificadoAdj,
    "{{declaranteQual}}": declaranteQual,
    "{{advFirma}}": advFirma,
    "{{advOab}}": advOab,
    "{{ao_DECLARANTE}}":
      clientGender === "F"
        ? "à <strong>DECLARANTE</strong>"
        : "ao <strong>DECLARANTE</strong>",
    "{{artigo_lc}}": clientGender === "F" ? "a" : "o",
    "{{pelo_lc}}": clientGender === "F" ? "pela" : "pelo",
    "{{artigo_unico}}": clientGender === "F" ? "única" : "único",
    "{{artigo_a}}": clientGender === "F" ? "a" : "",
  };
}

// Chaves cujo valor já é HTML pré-montado e seguro (declaranteQual escapa o
// texto livre do cliente na origem via ClientSchema.js/strongUpper antes de
// embrulhar em <strong>; ao_DECLARANTE é string estática com <strong> fixo).
// Tudo que não estiver aqui é texto puro e é escapado por padrão — fecha o
// mesmo vetor de XSS já corrigido em Procuração/Contrato (Art. 341/343).
const HTML_TRUSTED_VARS = new Set(["{{declaranteQual}}", "{{ao_DECLARANTE}}"]);

export function resolveText(text, vars) {
  let result = String(text || "");
  for (const [key, val] of Object.entries(vars)) {
    const safeVal = HTML_TRUSTED_VARS.has(key) ? String(val ?? "") : escapeHtml(val);
    result = result.split(key).join(safeVal);
  }
  return result;
}

// ── isBlockVisible — condição de visibilidade (Padrão Contrato/Procuração) ──
export function isDeclarationBlockVisible(block, reasonCodes = []) {
  const cond = String(block?.condition || "ALWAYS").trim();
  if (cond === "ALWAYS") return true;
  if (cond === "HAS_ANY_REASON")
    return Array.isArray(reasonCodes) && reasonCodes.length > 0;
  const match = cond.match(/^HAS_REASON_([A-F])$/);
  if (match)
    return Array.isArray(reasonCodes) && reasonCodes.includes(match[1]);
  return true;
}

// ── isChildVisible — para sub-cláusulas condicionais ─────────────────────────
export function isDeclarationChildVisible(child, reasonCodes = []) {
  return isDeclarationBlockVisible(child, reasonCodes);
}

// ── buildDeclarationHtml ──────────────────────────────────────────────────────
// Retorna apenas o CORPO do documento (sem título — o título fica no JSX com
// contract-title, igual ao ProcurationEngine / ContractEngine).
// Quando clientData é null, exibe marcadores de placeholder visuais.
// ── buildDeclarationHtmlFromTemplate ─────────────────────────────────────────
// Constrói o HTML a partir de um template carregado do Firestore.
// O template contém clauses[] com placeholders que são substituídos em runtime.
// Se template for null/undefined, retorna null → o caller usa o fallback hardcoded.
export function buildDeclarationHtmlFromTemplate({
  template,
  clientData,
  officeData,
  declarationType = "hipossuficiencia",
  reasonCodes = [],
  clauseOverrides = {},
}) {
  if (!template || !Array.isArray(template.clauses)) return null;

  const isPlaceholder = !clientData;
  const advNome = String(officeData?.name || "RODRIGO DEMETERCO").trim();
  const advOab = String(officeData?.oab || "113.938/PR").trim();
  const advFirma = advNome + " Advocacia";

  let declaranteQual;
  let qualificadoAdj = "qualificado";

  const clientGender = isPlaceholder
    ? ""
    : String(
        clientData?.grammaticalGender ||
          clientData?.qualification?.physical?.grammaticalGender ||
          "",
      )
        .trim()
        .toUpperCase();
  if (clientGender === "F") qualificadoAdj = "qualificada";

  const artigo = clientGender === "F" ? "a" : "o";
  const artigoUC = clientGender === "F" ? "A" : "O";
  const pelo = clientGender === "F" ? "pela" : "pelo";
  const doDecl = clientGender === "F" ? "da" : "do";

  if (isPlaceholder) {
    declaranteQual = "...";
  } else {
    const rawQual = String(generateQualificationText(clientData) || "").trim();
    declaranteQual = rawQual
      .replace(/,?\s*condição de saúde declarada:\s*[^,.]+/gi, "")
      .replace(/\.$/, "");
  }

  // Mapa de placeholders → valores
  const vars = {
    "{{artigo}}": artigo,
    "{{ARTIGO_UC}}": artigoUC,
    "{{pelo}}": pelo,
    "{{do}}": doDecl,
    "{{DO}}": doDecl.toUpperCase(),
    "{{DO_TITLE}}": clientGender === "F" ? "Da" : "Do",
    "{{qualificado}}": qualificadoAdj,
    "{{declaranteQual}}": declaranteQual,
    "{{advFirma}}": advFirma,
    "{{advOab}}": advOab,
    "{{ao_DECLARANTE}}":
      clientGender === "F"
        ? "à <strong>DECLARANTE</strong>"
        : "ao <strong>DECLARANTE</strong>",
    "{{artigo_lc}}": clientGender === "F" ? "a" : "o",
    "{{pelo_lc}}": clientGender === "F" ? "pela" : "pelo",
    "{{artigo_unico}}": clientGender === "F" ? "única" : "único",
    "{{artigo_a}}": clientGender === "F" ? "a" : "",
  };

  const replaceVars = (text) => {
    let result = String(text || "");
    for (const [key, val] of Object.entries(vars)) {
      result = result.split(key).join(val);
    }
    return result;
  };

  // Helpers de renderização (idênticos ao hardcoded)
  const clauseHeading = (num, title) =>
    `<h2 class="clause-heading"><span class="clause-heading-row" style="width:100%;display:flex;align-items:flex-start;flex-wrap:wrap;gap:0.25rem;"><span class="clause-number">Cláusula ${num}ª&nbsp;&ndash;</span>&nbsp;<span class="clause-title">${title}</span></span></h2>`;

  const clauseBlock = (num, headingHtml, bodyHtml) =>
    `<div class="clause-block" id="decl-clause-${num}" style="--macro-num:${num}; border-left:none; padding-left:0.5rem;">${headingHtml}<div class="clause-body mt-2">${bodyHtml}</div></div>`;

  const sc = (num, content, level = 0) => {
    const numCol = level === 0 ? "4.5ch" : "6.2ch";
    const indent = level > 0 ? "calc(4.5ch + 4px)" : "0";
    return `<div class="sub-clause-item has-manual-number" style="--subclause-indent:${indent}">
      <div class="clause-li-row" style="--clause-num-col:${numCol};">
        <span style="flex-shrink:0;font-weight:700;font-size:11pt;white-space:nowrap;color:var(--text-primary,#374151);">${num}</span>
        <span>${content}</span>
      </div>
    </div>`;
  };

  // Reasons (motivos de hipossuficiência)
  const reasons = Array.isArray(template.reasons)
    ? template.reasons
    : HIPOSSUFICIENCIA_REASONS;
  const selectedReasons = reasons.filter(
    ({ code }) => Array.isArray(reasonCodes) && reasonCodes.includes(code),
  );
  const reasonSubItems = selectedReasons
    .map(({ text }, idx) => sc(`2.2.${idx + 1}.`, replaceVars(text), 1))
    .join("");
  const reasonBlock =
    selectedReasons.length > 0
      ? sc("2.2.", "A hipossuficiência decorre faticamente de:") +
        reasonSubItems
      : "";

  // Renderizar cada cláusula do template
  const parts = template.clauses.map((clause) => {
    const num = clause.num;

    // Se há override do admin, usar o override
    if (num > 0 && clauseOverrides[num] !== undefined)
      return clauseOverrides[num];

    // Preâmbulo (num === 0)
    if (clause.isPreambulo && Array.isArray(clause.paragraphs)) {
      const body = clause.paragraphs
        .map(
          (p, i, arr) =>
            `<p class="contract-p"${i < arr.length - 1 ? ' style="margin-bottom:0.5rem;"' : ""}>${replaceVars(p)}</p>`,
        )
        .join("\n        ");
      return `
    <div class="clause-block preambulo-block" id="decl-preambulo" style="border-left:none;padding-left:0.5rem;margin-bottom:1.5rem;">
      <h2 class="clause-heading" style="font-variant:small-caps;"><span class="clause-heading-row" style="width:100%;display:flex;align-items:flex-start;flex-wrap:wrap;gap:0.25rem;"><span class="clause-title" style="font-size:11pt;">${replaceVars(clause.title)}</span></span></h2>
      <div class="clause-body mt-2">
        ${body}
      </div>
    </div>`;
    }

    // Cláusulas normais (num >= 1)
    const title = replaceVars(clause.title);
    let body = "";
    if (clause.introText) {
      body += `<p class="contract-p" style="margin-top:0.5rem; margin-bottom:0.5rem;">${replaceVars(clause.introText)}</p>`;
    }
    if (Array.isArray(clause.subClauses)) {
      body += clause.subClauses
        .map((sub) => sc(sub.num, replaceVars(sub.text), sub.level || 0))
        .join("");
    }
    if (clause.hasReasonBlock) {
      body += reasonBlock;
    }
    return clauseBlock(num, clauseHeading(String(num), title), body);
  });

  return `\n    ${parts.join("\n\n    ")}\n  `;
}

// ── buildDeclarationHtml (FALLBACK HARDCODED — não alterar) ─────────────────
// Retorna apenas o CORPO do documento (sem título — o título fica no JSX com
// contract-title, igual ao ProcurationEngine / ContractEngine).
// Quando clientData é null, exibe marcadores de placeholder visuais.
export function buildDeclarationHtml({
  clientData,
  officeData,
  declarationType = "hipossuficiencia",
  reasonCodes = [],
  clauseOverrides = {},
}) {
  const isPlaceholder = !clientData;

  // ── Dados do advogado ────────────────────────────────────────────────────
  const advNome = String(officeData?.name || "RODRIGO DEMETERCO").trim();
  const advOab = String(officeData?.oab || "113.938/PR").trim();
  const advFirma = advNome + " Advocacia";

  // ── Qualificação canônica (padrão declaranteQual — idêntico a outorganteQual) ─
  // Placeholder canônico: '...' — mesmo padrão da ProcurationEngine.
  let declaranteQual;
  let qualificadoAdj = "qualificado"; // default: masculino / gênero indefinido

  const clientGender = isPlaceholder
    ? ""
    : String(
        clientData?.grammaticalGender ||
          clientData?.qualification?.physical?.grammaticalGender ||
          "",
      )
        .trim()
        .toUpperCase();
  if (clientGender === "F") qualificadoAdj = "qualificada";

  const declaranteArtigo = clientGender === "F" ? "a" : "o";
  const declaranteArtigoUC = clientGender === "F" ? "A" : "O";
  const peloDeclArtigo = clientGender === "F" ? "pela" : "pelo";
  const doDeclArtigo = clientGender === "F" ? "da" : "do";

  if (isPlaceholder) {
    declaranteQual = "...";
  } else {
    const rawQual = String(generateQualificationText(clientData) || "").trim();
    // Remove "condição de saúde declarada: ..." — dado administrativo interno, não pertence à qualificação jurídica formal
    declaranteQual = rawQual
      .replace(/,?\s*condição de saúde declarada:\s*[^,.]+/gi, "")
      .replace(/\.$/, "");
  }

  // ── Helpers de renderização (padrão ContractEngine / ProcurationEngine) ─────
  const clauseHeading = (num, title) =>
    `<h2 class="clause-heading"><span class="clause-heading-row" style="width:100%;display:flex;align-items:flex-start;flex-wrap:wrap;gap:0.25rem;"><span class="clause-number">Cláusula ${num}ª&nbsp;&ndash;</span>&nbsp;<span class="clause-title">${title}</span></span></h2>`;

  const clauseBlock = (num, headingHtml, bodyHtml) =>
    `<div class="clause-block" id="decl-clause-${num}" style="--macro-num:${num}; border-left:none; padding-left:0.5rem;">${headingHtml}<div class="clause-body mt-2">${bodyHtml}</div></div>`;

  const sc = (num, content, level = 0) => {
    const numCol = level === 0 ? "4.5ch" : "6.2ch";
    const indent = level > 0 ? "calc(4.5ch + 4px)" : "0";
    return `<div class="sub-clause-item has-manual-number" style="--subclause-indent:${indent}">
      <div class="clause-li-row" style="--clause-num-col:${numCol};">
        <span style="flex-shrink:0;font-weight:700;font-size:11pt;white-space:nowrap;color:var(--text-primary,#374151);">${num}</span>
        <span>${content}</span>
      </div>
    </div>`;
  };

  const selectedReasons = HIPOSSUFICIENCIA_REASONS.filter(
    ({ code }) => Array.isArray(reasonCodes) && reasonCodes.includes(code),
  );

  const reasonSubItems = selectedReasons
    .map(({ text }, idx) => {
      let replaced = text
        .replace(/O\(A\)/g, declaranteArtigoUC)
        .replace(/o\(a\)/g, declaranteArtigo)
        .replace(/único\(a\)/g, clientGender === "F" ? "única" : "único")
        .replace(
          /mantenedor\(a\)/g,
          clientGender === "F" ? "mantenedora" : "mantenedor",
        )
        .replace(/pelo\(a\)/g, peloDeclArtigo)
        .replace(
          /(o|a|ao|à|do|da|pelo|pela|aos|às|dos|das|pelos|pelas)\s+declarante/gi,
          "$1 <strong>DECLARANTE</strong>",
        );
      return sc(`2.2.${idx + 1}.`, replaced, 1);
    })
    .join("");

  const reasonBlock =
    selectedReasons.length > 0
      ? sc("2.2.", "A hipossuficiência decorre faticamente de:") +
        reasonSubItems
      : "";

  return `
    <div class="clause-block preambulo-block" id="decl-preambulo" style="border-left:none;padding-left:0.5rem;margin-bottom:1.5rem;">
      <h2 class="clause-heading" style="font-variant:small-caps;"><span class="clause-heading-row" style="width:100%;display:flex;align-items:flex-start;flex-wrap:wrap;gap:0.25rem;"><span class="clause-title" style="font-size:11pt;">Preâmbulo e Diretrizes de Interpretação</span></span></h2>
      <div class="clause-body mt-2">
        <p class="contract-p" style="margin-bottom:0.5rem;">Considerando que a hipossuficiência econômica é causa legítima para o reconhecimento do direito à gratuidade da justiça, nos termos do art. 5º, inciso LXXIV, da Constituição Federal de 1988, e dos arts. 98 e seguintes da Lei n. 13.105/2015 (Código de Processo Civil);</p>
        <p class="contract-p" style="margin-bottom:0.5rem;">Considerando que a declaração de insuficiência de recursos, conforme previsto no art. 99, § 3º, da Lei n. 13.105/2015 (Código de Processo Civil), presume-se verdadeira até prova em contrário, impondo-se ${clientGender === "F" ? "à <strong>DECLARANTE</strong>" : "ao <strong>DECLARANTE</strong>"} responsabilidade civil por eventual má-fé, nos termos dos arts. 79 a 81 do mesmo Código de Processo Civil;</p>
        <p class="contract-p" style="margin-bottom:0.5rem;">${declaranteArtigoUC} <strong>DECLARANTE</strong> firma a presente declaração de hipossuficiência econômica com o propósito de fundamentar o pedido de gratuidade da justiça no processo a que se destina, visando garantir o acesso à justiça e a tutela de direitos fundamentais, em conformidade com a legislação vigente.</p>
        <p class="contract-p">Para fins de padronização e fluidez de leitura, a presente declaração utilizará o substantivo <strong>DECLARANTE</strong> com artigo e adjetivos concordantes com o gênero gramatical indicado na Cláusula 1ª — isto é, <strong>o DECLARANTE</strong> ou <strong>a DECLARANTE</strong> conforme o caso — para referir-se à pessoa que subscreve este documento. Toda a concordância verbal e nominal das cláusulas a seguir obedecerá estritamente a esta regra.</p>
        <p class="contract-p">Ressalva-se que a padronização de gênero gramatical aqui adotada tem finalidade exclusivamente técnica de uniformização redacional, não refletindo qualquer declaração sobre o gênero ${clientGender === "F" ? "da" : "do"} <strong>DECLARANTE</strong>, assegurando-se a validade da declaração independentemente de eventuais usos gramaticais divergentes da concordância tradicional.</p>
      </div>
    </div>

    ${
      clauseOverrides[1] !== undefined
        ? clauseOverrides[1]
        : clauseBlock(
            1,
            clauseHeading(
              "1",
              `${clientGender === "F" ? "Da" : "Do"} Declarante`,
            ),
            sc("1.1.", `<strong>DECLARANTE:</strong> ${declaranteQual}`),
          )
    }

    ${
      clauseOverrides[2] !== undefined
        ? clauseOverrides[2]
        : clauseBlock(
            2,
            clauseHeading(
              "2",
              "Da Insuficiência de Recursos e do Mínimo Existencial",
            ),
            sc(
              "2.1.",
              `${declaranteArtigoUC} <strong>DECLARANTE</strong>, devidamente ${qualificadoAdj} na Cláusula 1ª deste instrumento, <strong>AFIRMA</strong>, sob as penas da lei, para os fins e efeitos do art. 5º, inciso LXXIV, da Constituição Federal, c/c o art. 99, § 3º, da Lei n. 13.105/2015 (Código de Processo Civil), que <strong>se encontra em estado de insuficiência de liquidez financeira atual</strong>, consistente na impossibilidade de arcar com as custas, despesas processuais e honorários advocatícios sucumbenciais sem comprometimento do seu sustento próprio e de sua família. Referida insuficiência decorre da incompatibilidade entre a renda líquida disponível ${doDeclArtigo} <strong>DECLARANTE</strong> e os encargos do processo, e não se resume à ausência de patrimônio imobilizado.`,
            ) + reasonBlock,
          )
    }

    ${
      clauseOverrides[3] !== undefined
        ? clauseOverrides[3]
        : clauseBlock(
            3,
            clauseHeading(
              "3",
              "Do Consentimento Prévio e Autorização de Pesquisa Patrimonial",
            ),
            sc(
              "3.1.",
              `${declaranteArtigoUC} <strong>DECLARANTE</strong> <strong>AUTORIZA</strong>, de forma expressa e voluntária, para os fins deste processo, que o Juízo competente promova pesquisas patrimoniais nos sistemas institucionais do Poder Judiciário, incluindo o Sistema Nacional de Investigação Patrimonial e Recuperação de Ativos — <strong>SNIPER</strong> (CNJ), o <strong>INFOJUD</strong> (Receita Federal), o <strong>SISBAJUD</strong> (Banco Central) e o <strong>RENAJUD</strong> (SENATRAN), independentemente de prévia decretação de quebra de sigilo bancário ou fiscal. Este consentimento é prestado ${peloDeclArtigo} <strong>DECLARANTE</strong> por ter convicção plena da veracidade das informações, reconhecendo que as consultas realizadas poderão confirmar a lisura do presente pleito.`,
            ),
          )
    }

    ${
      clauseOverrides[4] !== undefined
        ? clauseOverrides[4]
        : clauseBlock(
            4,
            clauseHeading(
              "4",
              "Da Autorização de Juntada de Documentos e Conformidade com a LGPD",
            ),
            sc(
              "4.1.",
              `${declaranteArtigoUC} <strong>DECLARANTE</strong> <strong>AUTORIZA</strong>, para os fins do art. 99, § 2º, da Lei n. 13.105/2015 (Código de Processo Civil), que o escritório <strong>${advFirma}</strong>, OAB n. ${advOab}, junte aos presentes autos os documentos comprobatórios de sua condição econômica que se mostrarem necessários à instrução deste pedido de gratuidade, incluindo declarações de imposto de renda, extratos bancários, holerites e demais documentos financeiros. Para os fins da Lei n. 13.709/2018 (LGPD), esta autorização constitui, cumulativamente:`,
            ) +
              sc(
                "4.1.1.",
                `consentimento informado para tratamento de dados pessoais e financeiros (art. 7º, inciso I, da Lei n. 13.709/2018 — LGPD); e`,
                1,
              ) +
              sc(
                "4.1.2.",
                `exercício regular de direitos em processo judicial (art. 7º, inciso VI, da Lei n. 13.709/2018 — LGPD).`,
                1,
              ) +
              sc(
                "4.2.",
                `${declaranteArtigoUC} <strong>DECLARANTE</strong> está ciente de que, após o peticionamento eletrônico nos sistemas do Poder Judiciário (PJe, Projudi, Eproc), a custódia dos documentos juntados aos autos migra para o Estado, cessando a responsabilidade tecnológica do escritório por eventuais incidentes originados nos servidores dos tribunais.`,
              ),
          )
    }

    ${
      clauseOverrides[5] !== undefined
        ? clauseOverrides[5]
        : clauseBlock(
            5,
            clauseHeading(
              "5",
              'Da Assunção Exclusiva de Responsabilidade e <em style="font-style:italic">Firewall</em> Ético',
            ),
            `<p class="contract-p" style="margin-top:0.5rem; margin-bottom:0.5rem;">${declaranteArtigoUC} <strong>DECLARANTE</strong> está ciente de que:</p>` +
              sc(
                "5.1.",
                `O juiz <em>poderá</em> exigir a comprovação da insuficiência de recursos, intimando ${declaranteArtigo} <strong>DECLARANTE</strong> antes de qualquer indeferimento (art. 99, §§ 2º e 3º, da Lei n. 13.105/2015 — Código de Processo Civil);`,
              ) +
              sc(
                "5.2.",
                `A prestação de informações falsas ou a omissão dolosa de patrimônio configura <strong>litigância de má-fé</strong>, sujeitando ${declaranteArtigo} <strong>DECLARANTE</strong> às sanções dos arts. 79 a 81 da Lei n. 13.105/2015 (Código de Processo Civil) — incluindo multa de até 10% do valor da causa e indenização à parte adversa — e à responsabilidade civil por perdas e danos nos termos do art. 186 da Lei n. 10.406/2002 (Código Civil);`,
              ) +
              sc(
                "5.3.",
                `Os advogados do escritório <strong>${advFirma}</strong> atuaram com base exclusivamente nas informações fornecidas ${peloDeclArtigo} <strong>DECLARANTE</strong>, não lhes competindo a auditoria prévia de seu patrimônio; <strong>${declaranteArtigoUC} DECLARANTE assume integralmente e com exclusividade</strong> qualquer responsabilidade por inveracidades informadas, isentando os patronos de solidariedade em quaisquer sanções processuais decorrentes;`,
              ) +
              sc(
                "5.4.",
                `A presente declaração produz efeitos para o processo a que se destina, podendo ser exigida nova declaração em processos futuros.`,
              ),
          )
    }
  `;
}
